import { Head, Link, usePage } from "@inertiajs/react"
import { Check } from "lucide-react"
import {
  AccountHero,
  DesignThumb,
  bottlesText,
  orderMeta,
  orderTitle,
} from "@/components/account/AccountParts"
import { PublicShell } from "@/components/PublicShell"
import { EstimateLine } from "@/components/public/SelectionTray"
import { formatDateOnly, formatDay } from "@/lib/dates"
import { formatPence } from "@/lib/money"
import { cn } from "@/lib/utils"
import type { AccountOrder, AccountOrderProps, AccountOrderStep, EnquiryStatus, PageProps } from "@/types/inertia"
import "@/styles/public-order.css"
import "@/styles/public-account.css"

// One enquiry's page (/account/orders/:id, M6 decision 13): where it is now,
// the five-step timeline, the wines (prices only when known), the designs and
// artwork that rode with it, the message and the contact details. Pipeline
// (won/lost) and internal notes are Duncan's and never reach this page.

/** One sentence for the current step; the status mails say the same. */
function statusSentence(status: EnquiryStatus, noun: string) {
  switch (status) {
    case "received":
      return `Your ${noun} is with our team, and we will be in touch personally about the next step.`
    case "in_design":
      return "Our studio is now working on your label design, and we will share it with you before anything is printed."
    case "approved":
      return "Your label design is approved, so your order can now move on to production."
    case "in_production":
      return "Your bottles are now being labelled and prepared for delivery."
    case "delivered":
      return "Your order has been delivered. Thank you for choosing SOH GD Wines; we hope every bottle is enjoyed."
  }
}

// A consultation's approximate bottle count rides on its message's first line
// (EnquiriesController); it is shown on its own, so the message leaves it out.
const APPROXIMATE_BOTTLES = /^Approximate bottles: \d+\s*/

function customerMessage(order: AccountOrder) {
  const message = order.message ?? ""
  const text = order.enquiry_type === "consultation" ? message.replace(APPROXIMATE_BOTTLES, "") : message
  return text.trim() || null
}

export default function AccountOrderPage() {
  const { props } = usePage<PageProps<AccountOrderProps>>()
  const { order } = props
  const title = orderTitle(order)
  const noun = order.enquiry_type === "order" ? "order" : "enquiry"
  const description = `Where your ${noun} #${order.id} with SOH GD Wines is now: every step from received to delivered, with the wines, label designs and details you sent us.`

  return (
    <PublicShell>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>

      <AccountHero
        titleId="order-title"
        back={{ href: "/account/orders", label: "My orders" }}
        eyebrow={order.enquiry_type === "order" ? "Order enquiry" : "Consultation enquiry"}
        title={title}
        lede={orderMeta(order)}
      />

      <section className="band band-charcoal acct-band" aria-labelledby="order-title">
        <div className="shell">
          <section className="acct-now" aria-labelledby="order-now-title">
            <div className="eyebrow">Where it is now</div>
            <h2 id="order-now-title">{order.status_label}</h2>
            <p>{statusSentence(order.status, noun)}</p>
            <Timeline steps={order.steps} />
          </section>

          <div className="acct-order-grid">
            <div className="min-w-0">
              <OrderContents order={order} />
              <OrderMessage order={order} />
              <OrderDesigns order={order} />
            </div>
            <OrderDetails order={order} />
          </div>
        </div>
      </section>
    </PublicShell>
  )
}

function Timeline({ steps }: { steps: AccountOrderStep[] }) {
  return (
    <ol className="acct-timeline" aria-label="Order progress">
      {steps.map((step) => (
        <li
          key={step.key}
          className={cn("acct-step", `is-${step.state}`)}
          aria-current={step.current ? "step" : undefined}
        >
          <span className="acct-step-mark" aria-hidden="true">
            {step.state === "done" && <Check strokeWidth={2.5} />}
          </span>
          <span className="acct-step-body">
            <span className="acct-step-label">{step.label}</span>
            <span className="acct-step-date">
              {step.at ? formatDay(step.at) : step.state === "done" ? "Done" : "To come"}
            </span>
            <span className="sr-only">
              {step.state === "done" ? " (done)" : step.state === "current" ? " (where it is now)" : " (to come)"}
            </span>
          </span>
        </li>
      ))}
    </ol>
  )
}

function OrderContents({ order }: { order: AccountOrder }) {
  if (order.lines.length === 0) {
    return (
      <section aria-labelledby="order-contents-title">
        <div className="eyebrow">What you sent us</div>
        <h2 id="order-contents-title">Your enquiry</h2>
        <p className="mt-4 max-w-[56ch]">
          {order.bottle_count !== null
            ? `About ${bottlesText(order.bottle_count)}. We will talk through the wines with you at consultation.`
            : "We will talk through the wines and the number of bottles with you at consultation."}
        </p>
      </section>
    )
  }

  return (
    <section aria-labelledby="order-contents-title">
      <div className="eyebrow">What you sent us</div>
      <h2 id="order-contents-title" className="mb-8">
        Your selection
      </h2>
      <ul className="ord-thanks-list">
        {order.lines.map((line) => (
          <li key={line.wine_id}>
            <span className="min-w-0">
              <span className="ord-wine-name">{line.wine_name}</span>
              <span className="ord-wine-meta">
                {line.producer} · {line.wine_type_label}
                {line.unit_price_pence !== null && ` · ${formatPence(line.unit_price_pence)} a bottle`}
              </span>
            </span>
            <span className="text-right whitespace-nowrap">
              <span className="ord-price block">{bottlesText(line.bottle_count)}</span>
              {line.line_total_pence !== null && (
                <span className="ord-wine-meta">{formatPence(line.line_total_pence)}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
      <div className="ord-thanks-total">
        <span className="ord-overline">Total</span>
        <span className="ord-summary-count">{bottlesText(order.bottle_count ?? 0)}</span>
      </div>
      <EstimateLine pence={order.estimate_pence} className="mt-4 text-right" />
    </section>
  )
}

function OrderMessage({ order }: { order: AccountOrder }) {
  const message = customerMessage(order)
  if (!message && !order.artwork_filename) return null

  return (
    <section className="acct-message" aria-labelledby="order-message-title">
      <h3 id="order-message-title">Your message</h3>
      {message && <p className="mt-4">{message}</p>}
      {order.artwork_filename && (
        <p className="mt-4">
          Artwork sent with your enquiry: <strong>{order.artwork_filename}</strong>
        </p>
      )}
    </section>
  )
}

function OrderDesigns({ order }: { order: AccountOrder }) {
  if (order.designs.length === 0) return null

  return (
    <section className="acct-message" aria-labelledby="order-designs-title">
      <h3 id="order-designs-title" className="mb-6">
        Your label designs
      </h3>
      <ul className="acct-order-designs">
        {order.designs.map((design) => (
          <li key={design.id} className="min-w-0">
            <DesignThumb
              title={design.title}
              previewUrl={design.preview_url}
              bottleType={design.bottle_type}
              href={design.studio_url}
            />
            <span className="acct-meta">{design.title}</span>
            {design.studio_url && (
              <Link href={design.studio_url} className="acct-action">
                Open in the studio<span className="sr-only"> {design.title}</span>
              </Link>
            )}
          </li>
        ))}
      </ul>
      <p className="ord-submit-note mt-6">Every label is subject to graphic design approval by our team.</p>
    </section>
  )
}

function OrderDetails({ order }: { order: AccountOrder }) {
  const { contact } = order
  const rows: [string, string | null][] = [
    ["Name", contact.name],
    ["Company", contact.company],
    ["Email", contact.email],
    ["Phone", contact.phone],
    ["Occasion", order.event_type_label],
    ["Date required", order.event_date ? formatDateOnly(order.event_date) : null],
    ["Sent", formatDay(order.created_at)],
  ]

  return (
    <aside className="self-start" aria-labelledby="order-details-title">
      <div className="eyebrow">Your details</div>
      <h2 id="order-details-title" className="mb-8">
        As you sent them
      </h2>
      <dl className="acct-facts">
        {rows
          .filter(([, value]) => value)
          .map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
      </dl>
      <p className="ord-submit-note mt-6">
        Something to change? Reply to any email from us, or <Link href="/contact">get in touch</Link>.
      </p>
    </aside>
  )
}
